import './TaskDetail.css';
import React, { useState } from 'react';
import type { Task, Agent, Activity, Document, TaskStatus } from '../types';
import { X, MessageSquare, Paperclip, Clock, User, Tag, Send, Copy, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface TaskDetailProps {
  task: Task;
  agents: Agent[];
  activities: Activity[];
  documents: Document[];
  onClose: () => void;
  onStatusChange: (status: TaskStatus) => void;
  onDelete: (taskId: string) => void;
}

const statusOptions: { value: TaskStatus; label: string; color: string }[] = [
  { value: 'inbox', label: 'To Do', color: '#8B7355' },
  { value: 'blocked', label: 'Blocked', color: '#8B0000' },
  { value: 'in_progress', label: 'In Progress', color: '#CD853F' },
  { value: 'review', label: 'Review', color: '#B8860B' },
  { value: 'done', label: 'Done', color: '#228B22' },
];

const TaskDetail: React.FC<TaskDetailProps> = ({
  task,
  agents,
  activities,
  documents,
  onClose,
  onStatusChange,
  onDelete,
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'comments' | 'docs'>('details');
  const [newComment, setNewComment] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  const getAgentName = (id: string) => agents.find(a => a.id === id)?.name || 'Unknown';
  const getAgentEmoji = (id: string) => agents.find(a => a.id === id)?.emoji || '👤';

  const handleStatusChange = (newStatus: TaskStatus) => {
    onStatusChange(newStatus);
  };

  const handleAddComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim()) {
      // In real implementation, this would add to messages
      setNewComment('');
    }
  };

  const handleCopy = async () => {
    const text = `${task.title}\n\n${task.description || ''}`.trim();
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1500);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1500);
    }
  };

  const handleDelete = () => {
    if (confirm('Delete this task? This cannot be undone.')) {
      onDelete(task.id);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-xl task-detail-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="task-detail-header">
          <div className="task-title-section">
            <div className={`priority-indicator priority-${task.priority}`} />
            <h2>{task.title}</h2>
          </div>
          <div className="task-detail-actions">
            <button className="btn-icon" onClick={handleCopy} title="Copy task text">
              <Copy size={18} />
            </button>
            <button className="btn-icon danger" onClick={handleDelete} title="Delete task">
              <Trash2 size={18} />
            </button>
            <button className="btn-icon" onClick={onClose} title="Close">
              <X size={20} />
            </button>
          </div>
        </div>
        {copySuccess && (
          <div className="copy-toast">Copied to clipboard</div>
        )}

        {/* Status Bar */}
        <div className="task-status-bar">
          <span>Status:</span>
          <div className="status-selector">
            {statusOptions.map(option => (
              <button
                key={option.value}
                className={`status-btn ${task.status === option.value ? 'active' : ''}`}
                style={{ 
                  backgroundColor: task.status === option.value ? option.color : 'transparent',
                  borderColor: option.color 
                }}
                onClick={() => handleStatusChange(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="task-tabs">
          <button 
            className={activeTab === 'details' ? 'active' : ''}
            onClick={() => setActiveTab('details')}
          >
            Details
          </button>
          <button 
            className={activeTab === 'comments' ? 'active' : ''}
            onClick={() => setActiveTab('comments')}
          >
            Comments ({activities.length})
          </button>
          <button 
            className={activeTab === 'docs' ? 'active' : ''}
            onClick={() => setActiveTab('docs')}
          >
            Documents ({documents.length})
          </button>
        </div>

        {/* Content */}
        <div className="task-detail-content">
          {activeTab === 'details' && (
            <div className="tab-details">
              <div className="detail-section">
                <h4>Description</h4>
                <p className="task-description">{task.description || 'No description provided.'}</p>
              </div>

              <div className="detail-grid">
                <div className="detail-item">
                  <User size={16} />
                  <div>
                    <label>Assignees</label>
                    <div className="assignee-list">
                      {task.assigneeIds.length > 0 ? (
                        task.assigneeIds.map(id => (
                          <span key={id} className="assignee-tag">
                            {getAgentEmoji(id)} {getAgentName(id)}
                          </span>
                        ))
                      ) : (
                        <span className="unassigned">Unassigned</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="detail-item">
                  <Tag size={16} />
                  <div>
                    <label>Tags</label>
                    <div className="tag-list">
                      {task.tags.length > 0 ? (
                        task.tags.map(tag => (
                          <span key={tag} className="tag">{tag}</span>
                        ))
                      ) : (
                        <span className="no-tags">No tags</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="detail-item">
                  <Clock size={16} />
                  <div>
                    <label>Created</label>
                    <span>{formatDistanceToNow(new Date(task.createdAt), { addSuffix: true })}</span>
                  </div>
                </div>

                <div className="detail-item">
                  <Clock size={16} />
                  <div>
                    <label>Last Updated</label>
                    <span>{formatDistanceToNow(new Date(task.updatedAt), { addSuffix: true })}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'comments' && (
            <div className="tab-comments">
              <div className="comments-list">
                {activities.length === 0 ? (
                  <div className="empty-comments">
                    <MessageSquare size={32} />
                    <p>No comments yet</p>
                    <span>Start the conversation by adding a comment below</span>
                  </div>
                ) : (
                  activities.map(activity => (
                    <div key={activity.id} className="comment-item">
                      <div className="comment-avatar">
                        {getAgentEmoji(activity.agentId)}
                      </div>
                      <div className="comment-content">
                        <div className="comment-header">
                          <span className="comment-author">{getAgentName(activity.agentId)}</span>
                          <span className="comment-time">
                            {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
                          </span>
                        </div>
                        <p className="comment-text">{activity.message}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <form className="comment-form" onSubmit={handleAddComment}>
                <input
                  type="text"
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                />
                <button type="submit" className="btn-icon">
                  <Send size={18} />
                </button>
              </form>
            </div>
          )}

          {activeTab === 'docs' && (
            <div className="tab-docs">
              {documents.length === 0 ? (
                <div className="empty-docs">
                  <Paperclip size={32} />
                  <p>No documents attached</p>
                  <span>Attach documents to keep all related files in one place</span>
                </div>
              ) : (
                <div className="attached-docs">
                  {documents.map(doc => (
                    <div key={doc.id} className="attached-doc">
                      <Paperclip size={16} />
                      <span className="doc-title">{doc.title}</span>
                      <span className="doc-type">{doc.type}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetail;
